<template>
  <router-link
    :class="[
      'labs-collection-card',
      isListViewStyle && 'labs-collection-card--list',
    ]"
    :to="{
      name: 'collection-detail',
      params: { id: collection.objectID ? collection.objectID : collection.id },
    }"
  >
    <div
      :class="[
        'labs-collection-card__container',
        isListViewStyle && 'labs-collection-card__container--list',
      ]"
    >
      <div class="labs-collection-card__container__badges">
        <LockedContentHeader v-if="lockedContent" />
        <div
          v-if="showTimeAgoBadge"
          :class="[
            'labs-collection-card__container__badges__added-time-ago',
            !lockedContent &&
              'labs-collection-card__container__badges__added-time-ago--unlocked',
          ]"
        >
          {{ addedTimeAgoLabel }}
        </div>
      </div>
      <div
        :class="[
          'labs-collection-card__container__title',
          isListViewStyle && 'labs-collection-card__container__title--list',
        ]"
      >
        {{ collection.name }}
      </div>
      <div
        :class="[
          'labs-collection-card__container__details',
          isListViewStyle && 'labs-collection-card__container__details--list',
        ]"
      >
        <div
          :class="[
            'labs-collection-card__container__details-pill-wrapper',
            isListViewStyle &&
              'labs-collection-card__container__details-pill-wrapper--list',
          ]"
        >
          <PillBadge translucid sober>
            <strong>{{ totalLabs }}</strong> HANDS-ON LABS
          </PillBadge>
        </div>
        <CollectionDetailInfo
          :class="
            isListViewStyle && 'labs-collection-card__container--only-mobile'
          "
          :isListViewStyle="isListViewStyle"
          :collection="collection"
          :learningAreaNames="learningAreaNames"
        />
      </div>
      <SeparatorBorder
        v-if="viewStyle === 'tile'"
        gradient
        class="labs-collection-card__container-separator"
      />
      <div
        v-tooltip="{
          content: showDescriptionTooltip ? parsedDescription : '',
          placement: 'bottom-center',
          classes: 'lab-card__tooltip',
        }"
        :class="[
          'labs-collection-card__container-description',
          isListViewStyle &&
            'labs-collection-card__container-description--list',
        ]"
      >
        <span v-html="shortdescription"></span>
        <CollectionDetailInfo
          v-if="isListViewStyle"
          class="labs-collection-card__container--not-mobile"
          :isListViewStyle="isListViewStyle"
          :collection="collection"
          :learningAreaNames="learningAreaNames"
        />
      </div>
      <div
        v-if="visibleTags.length"
        :class="[
          'labs-collection-card__tags-wrapper',
          isListViewStyle && 'labs-collection-card__tags-wrapper--list',
        ]"
      >
        <div
          v-for="tag in visibleTags"
          :key="tagKey(tag)"
          class="labs-collection-card__tag"
          @click="handleTagClick($event, tag)"
        >
          <PillBadge
            v-tooltip="{
              content: showTooltips ? `Search by ${tagName(tag)}` : '',
              placement: 'right-center',
            }"
            translucid
            sober
            :class="[
              'labs-collection-card__tag-pill',
              clickableTags && 'labs-collection-card__tag-pill--clickable',
            ]"
          >
            {{ tagName(tag) }}
          </PillBadge>
        </div>
        <div v-if="hiddenTags.length" class="labs-collection-card__tag">
          <PillBadge
            v-tooltip="{
              content: hiddenTags
                .map(tag => `<div>${tagName(tag)}</div>`)
                .join(''),
              placement: 'right-center',
            }"
            class="labs-collection-card__tag-pill"
            translucid
            sober
          >
            +{{ hiddenTags.length }}
          </PillBadge>
        </div>
      </div>
    </div>
  </router-link>
</template>

<script>
import { formatDistanceToNow, differenceInDays } from 'date-fns'
import PillBadge from '@/components/shared/PillBadge'
import { getAllLearningAreas } from '@/helpers'
import SeparatorBorder from '@/components/shared/SeparatorBorder.vue'
import LockedContentHeader from '../properties/LockedContentHeader.vue'
import CollectionDetailInfo from './CollectionDetailInfo.vue'

export default {
  name: 'LabsCollectionCard',
  components: {
    PillBadge,
    SeparatorBorder,
    LockedContentHeader,
    CollectionDetailInfo,
  },
  inject: ['getWindowWidth'],
  props: {
    collection: {
      type: Object,
      required: true,
    },
    viewStyle: {
      type: String,
      required: false,
      default: 'tile',
    },
    showTooltips: {
      type: Boolean,
      required: false,
      default: true,
    },
    lockedContent: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    clickableTags() {
      return !!this.$listeners['apply-tag-filter']
    },

    visibleTags() {
      return this.collection.tags.slice(0, this.numberOfTagsToShow - 1)
    },
    hiddenTags() {
      return this.collection.tags.slice(this.numberOfTagsToShow - 1)
    },
    learningAreaNames() {
      const la = getAllLearningAreas(this.collection)
      return la
    },
    formatedDateFromApi() {
      // TODO reuse common code with collection mixin
      const published_date = this.collection?.published_date

      if (published_date) {
        if (typeof published_date === 'string') {
          // comes string from content-api
          return new Date(published_date)
        } else {
          // comes number from algolia
          return new Date(published_date * 1000)
        }
      } else return null
    },
    addedTimeAgoLabel() {
      // TODO reuse common code with collection mixin

      const timeAgo = formatDistanceToNow(this.formatedDateFromApi, {
        addSuffix: true,
      })

      return `Added ${timeAgo}`
    },
    showTimeAgoBadge() {
      const days = differenceInDays(
        new Date(),
        new Date(this.formatedDateFromApi)
      )

      return days <= 30
    },
    totalLabs() {
      return this.collection.summary.total_labs
    },
    numberOfTagsToShow() {
      if (this.getWindowWidth() <= 375) {
        return 2
      }
      return this.isListViewStyle ? 7 : 3
    },
    isListViewStyle() {
      return this.viewStyle === 'list'
    },
    shortdescription() {
      const max = this.isListViewStyle ? 320 : 200
      return this.parsedDescription.length > max
        ? `${this.parsedDescription
            .slice(0, max)
            .split(' ')
            .slice(0, -1)
            .join(' ')}...`
        : this.parsedDescription
    },
    parsedDescription() {
      return this.collection?.description
        ? this.collection?.description
            .replace(/(<([^>]+)>)/gi, '')
            .replace(/(\r\n|\n|\r)/gm, ' ')
            .trim()
        : ''
    },
    showDescriptionTooltip() {
      return this.parsedDescription.length > this.shortdescription.length
    },
  },
  methods: {
    handleTagClick($event, tag) {
      $event.preventDefault()
      this.$emit('apply-tag-filter', tag)
    },
    tagKey(tag) {
      return tag.id || tag
    },
    tagName(tag) {
      return tag.name || tag
    },
  },
}
</script>

<style lang="scss" scoped>
.labs-collection-card {
  padding: 30px 20px;
  transition: 1s all ease;
  text-align: left;
  white-space: normal;
  position: relative;

  border-radius: 20px;
  border: 1px solid rgba(187, 133, 255, 0.2);
  background: rgba(71, 63, 63, 0.2);
  backdrop-filter: blur(15px);
  @include mobile {
    padding: 30px;
  }
  @include desktop {
    padding: 40px;
  }

  &--list {
    @include desktop {
      padding: 30px 40px;
    }
  }

  &:hover {
    border: 1px solid rgba(187, 133, 255, 0.5);
    background: var(--glass-extra-light);
  }
  &:active {
    border: 1px solid rgba(187, 133, 255, 0.2);
    background: rgba(71, 63, 63, 0.2);
    backdrop-filter: blur(15px);
  }

  &__container {
    display: flex;
    flex-direction: column;
    height: 100%;

    &--list {
      gap: 10px;
    }

    &__badges {
      display: flex;
      justify-content: right;
      gap: 5px;
      top: 0px;
      right: 0;
      left: 0;
      position: absolute;
      border-radius: 20px 20px 0 0;
      overflow: hidden;

      color: var(--white);
      text-align: center;
      font-size: rem(10px);
      font-style: normal;
      font-weight: 450;
      line-height: rem(12px);
      @include desktop {
        font-size: 0.75rem;
        line-height: 0.875rem;
      }

      &__added-time-ago {
        display: flex;
        padding: 5px 20px;
        align-items: center;
        background: var(--secondary-promo-purple);

        &--unlocked {
          border-bottom-left-radius: 10px;
        }
      }
    }

    &__title {
      color: var(--white);
      font-size: 16px;
      font-style: normal;
      font-weight: 500;
      line-height: 23px;
      min-height: 46px;

      @include mobile {
        font-size: 24px;
        font-weight: 450;
        line-height: 32px;
        min-height: 64px;
      }
      @include desktop {
        font-size: 30px;
        font-weight: 500;
        line-height: 38px;
        min-height: 76px;
      }

      &:not(.labs-collection-card__container__title--list) {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      &--list {
        min-height: unset;
      }
    }
    &__details {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
      margin-top: 20px;
      @include mobile {
        margin-top: 25px;
      }

      &--list {
        margin-top: 0px;
      }

      &-pill-wrapper {
        width: 100%;

        @include mobile {
          width: auto;
        }

        @include desktop {
          width: 100%;
        }

        &--list {
          @include desktop {
            width: fit-content;
          }
        }
      }
    }

    &-separator {
      margin: 15px 0;

      @include mobile {
        margin: 20px 0;
      }
    }
    &-description {
      font-size: rem(12px);
      font-style: normal;
      font-weight: 450;
      line-height: rem(14px);
      color: var(--bright-gray);
      margin-bottom: 15px;
      min-height: 72px;
      height: fit-content;
      overflow: hidden;

      @include mobile {
        height: 84px;
      }

      @include tablet {
        min-height: 56px;
        height: fit-content;
      }

      &--list {
        min-height: unset;
        @include tablet {
          display: flex;
          height: fit-content;
          justify-content: space-between;
          gap: 20px;
          margin-bottom: unset;

          span {
            display: flex;
            align-content: center;
            flex-wrap: wrap;
            max-width: 890px;
          }
        }
      }
    }

    &--not-mobile {
      display: none;
      @include tablet {
        display: flex;
      }
    }
    &--only-mobile {
      @include tablet {
        display: none;
      }
    }
  }

  &__tags {
    &-wrapper {
      display: flex;
      gap: 5px;

      &--list {
        overflow: auto;
        text-overflow: ellipsis;
        max-width: 890px;
      }

      &__tag-pill {
        white-space: nowrap;
        &--clickable {
          &:hover {
            cursor: pointer;
          }
        }

        &:last-child {
          color: red;
        }
      }
    }
    .filter-tags__tag-list {
      margin: 0 !important;
    }
  }
  ::v-deep .difficulty__label {
    font-size: 12px;
    line-height: 14px;
    font-family: var(--font-book);
  }
}
</style>
